import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Camera, MessageCircle } from "lucide-react";

export const FriendActivityNotifications = () => {
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user || null;
    }
  });

  const { data: recentActivity } = useQuery({
    queryKey: ['friend-activity', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      
      // Get recent friend activities
      const { data: friends } = await supabase
        .rpc('get_user_friends', { user_id: currentUser.id });

      if (!friends?.length) return [];

      const friendIds = friends.map(f => f.friend_id);

      // Get recent posts from friends (last 24 hours)
      const { data: recentPosts } = await supabase
        .from('posts')
        .select(`
          id,
          user_id,
          content,
          created_at,
          profiles!posts_user_id_fkey(username, avatar_url)
        `)
        .in('user_id', friendIds)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(3);

      // Get new friends who joined recently
      const { data: newFriends } = await supabase
        .from('user_relationships')
        .select(`
          created_at,
          following_id,
          profiles!user_relationships_following_id_fkey(username, avatar_url)
        `)
        .eq('follower_id', currentUser.id)
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(2);

      return {
        recentPosts: recentPosts || [],
        newFriends: newFriends || []
      };
    },
    enabled: !!currentUser?.id
  });

  const activity = recentActivity as { recentPosts: any[], newFriends: any[] } | undefined;

  if (!activity?.recentPosts?.length && !activity?.newFriends?.length) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
      <CardContent className="p-4 space-y-3">
        <h4 className="font-medium text-green-900 flex items-center gap-2">
          <UserPlus className="w-4 h-4" />
          Friend Activity
        </h4>
        
        {activity?.newFriends?.map((friend, index) => (
          <div key={index} className="flex items-center gap-3 text-sm">
            <Avatar className="w-6 h-6">
              <AvatarImage 
                src={friend.profiles?.avatar_url || "https://github.com/shadcn.png"} 
                alt={friend.profiles?.username || "User"} 
              />
              <AvatarFallback>{friend.profiles?.username?.[0]?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
            <span className="text-green-700">
              <strong>{friend.profiles?.username}</strong> joined your network
            </span>
          </div>
        ))}

        {activity?.recentPosts?.slice(0, 2).map((post, index) => (
          <div key={index} className="flex items-center gap-3 text-sm">
            <Avatar className="w-6 h-6">
              <AvatarImage 
                src={post.profiles?.avatar_url || "https://github.com/shadcn.png"} 
                alt={post.profiles?.username || "User"} 
              />
              <AvatarFallback>{post.profiles?.username?.[0]?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
            <span className="text-green-700">
              <strong>{post.profiles?.username}</strong> shared an update
            </span>
            <Camera className="w-3 h-3 text-green-600" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};